import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  Loader2,
  Mail,
  Phone,
  Clock,
  AlertTriangle,
  XCircle,
  UserCheck,
  ExternalLink,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { trackEvent } from "@/mock";
import { analyticsApi, crmApi } from "@/services/phase1Api";
import { normalizeStage, stageOrder, PIPELINE_STAGES } from "../appShared";
import { STAGE_BADGE, JOURNEY_STEPS, journeyIndexFor, isCustomerStage } from "./adminStages";
import { fmtUSDCompact, fmtRelativeDate } from "./adminTheme";
import { ProspectAssessment, STAGE_TRANSITIONS } from "../PipelineScreen";
import FileAttachments from "@/components/FileAttachments";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────
// Stage Action Buttons — re-themed for the new Dark Navy & Gold workspace
// ─────────────────────────────────────────────────────────────

const ACTION_VARIANTS = {
  primary: "bg-[#0B1628] text-white hover:bg-[#152038]",
  gold: "text-white shadow-[0_4px_12px_rgba(199,154,59,0.30)] hover:opacity-95",
  payment: "bg-[#16a34a] text-white hover:bg-[#15803d]",
  destructive: "border border-[#fecaca] bg-[#fef2f2] text-[#b91c1c] hover:bg-[#fee2e2]",
  secondary: "border border-[#e2e8f0] bg-white text-[#475569] hover:bg-[#f8fafc]",
};

const ActionIcon = ({ variant, target }) => {
  if (variant === "payment") return <Check className="mr-2 h-4 w-4" />;
  if (variant === "destructive") {
    if (target === "DISQUALIFIED" || target === "CHURNED") return <XCircle className="mr-2 h-4 w-4" />;
    if (target === "NO_SHOW") return <Clock className="mr-2 h-4 w-4" />;
    if (target === "AT_RISK") return <AlertTriangle className="mr-2 h-4 w-4" />;
    return null;
  }
  if (variant === "primary" || variant === "gold") return <ArrowRight className="mr-2 h-4 w-4" />;
  return null;
};

const StageActionsBlock = ({ session, prospect, onRefresh, assessmentStatus, pricing }) => {
  const [busy, setBusy] = useState(false);
  const stage = normalizeStage(prospect.stage);
  const config = STAGE_TRANSITIONS[stage] || {};
  const transitions = config.actions || [];
  const isAdmin = session.user.role === "admin";
  const { hasCompletedInitial, hasCompletedDetailed } = assessmentStatus || {};
  const hasConsultant = !!prospect.assigned_consultant_id;

  const fmtAmt = (amt) => `$${Math.round(amt).toLocaleString()}`;
  const assessmentAmt = pricing?.pricing?.assessment?.amount;
  const implementationAmt = pricing?.pricing?.implementation?.amount;
  const retainerPlans = pricing?.retainer_plans || [];

  const labelFor = (t) => {
    if (t.isPortalAccess && prospect.provisioned_tenant_id) return "Resend Portal Access";
    if (t.productKey === "project" && implementationAmt) return `Record Payment — Implementation Project (${fmtAmt(implementationAmt)})`;
    if (t.productKey === "retainer" && retainerPlans.length === 1) {
      return `Record Payment — Monthly Retainer (${fmtAmt(retainerPlans[0].monthly_price)}/mo)`;
    }
    if (t.productKey === "detailed" && assessmentAmt) return `Record Payment — Paid Assessment (${fmtAmt(assessmentAmt)})`;
    return t.label;
  };

  const isGated = (t) => {
    if (t.requiresAssessment === "initial" && !hasCompletedInitial) return true;
    if (t.requiresAssessment === "detailed" && !hasCompletedDetailed) return true;
    if (t.requiresScopeConfirm) return "scope";
    if (t.variant === "payment") return false;
    const consultantRequired = ["ASSESSMENT_PURCHASED", "ASSESSMENT_IN_PROGRESS", "ASSESSMENT_DELIVERED"].includes(t.target);
    if (consultantRequired && !hasConsultant) return "consultant";
    return false;
  };

  const handleAdvance = async (t) => {
    setBusy(true);
    try {
      if (t.isPortalAccess) {
        const res = await fetch(`${BACKEND}/api/prospects/${prospect.id}/send-portal-access`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-id": session.tenant?.id || "tenant_1",
            "x-user-id": session.user?.id || "admin",
            "x-user-role": session.user?.role || "admin",
          },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed");
        if (data.invite_link) await navigator.clipboard.writeText(data.invite_link).catch(() => {});
        toast.success("Portal access sent", { description: `Link copied: ${data.invite_link}` });
      } else if (t.isProceedToSOW) {
        const res = await fetch(`${BACKEND}/api/prospects/${prospect.id}/proceed-to-sow`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-id": session.tenant?.id || "tenant_1",
            "x-user-id": session.user?.id || "admin",
            "x-user-role": session.user?.role || "admin",
          },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed");
        toast.success("Tenant provisioned & Scope of Services generated", { description: data.message?.substring(0, 80) });
      } else if (t.productKey) {
        await crmApi.mockPayment({ prospect_id: prospect.id, product_key: t.productKey }, session);
        trackEvent("payment_recorded", { prospect_id: prospect.id, product: t.productKey });
        analyticsApi.track("payment_recorded", { prospect_id: prospect.id, product: t.productKey }, session);
        toast.success("Payment recorded", { description: `Stage advanced to ${PIPELINE_STAGES[t.target]?.label || t.target}` });
      } else {
        await crmApi.updateProspect({ prospect_id: prospect.id, stage: t.target }, session);
        trackEvent("stage_advanced", { prospect_id: prospect.id, to: t.target });
        analyticsApi.track("stage_advanced", { prospect_id: prospect.id, to: t.target }, session);
        toast.success("Stage updated", { description: `Moved to ${PIPELINE_STAGES[t.target]?.label || t.target}` });
      }
      onRefresh();
    } catch (e) {
      toast.error("Action failed", { description: e?.message || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  if (!transitions.length && !(hasCompletedInitial && !prospect.portal_access_sent_at)) return null;

  // Auto-surface "Send Portal Access" shortcut if preliminary assessment is done
  // but the consultant hasn't manually advanced stage yet.
  const shouldOfferPortalAccess = hasCompletedInitial && !prospect.provisioned_tenant_id && stage !== "FREE_ASSESSMENT_COMPLETE" && !transitions.some((t) => t.isPortalAccess);

  const handleSendPortalShortcut = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND}/api/prospects/${prospect.id}/send-portal-access`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-tenant-id": session.tenant?.id || "tenant_1",
          "x-user-id": session.user?.id || "admin",
          "x-user-role": session.user?.role || "admin",
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      if (data.invite_link) await navigator.clipboard.writeText(data.invite_link).catch(() => {});
      toast.success("Portal access sent", { description: `Link copied: ${data.invite_link}` });
      onRefresh();
    } catch (e) {
      toast.error("Failed to send portal access", { description: e?.message || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#e2e8f0] bg-white p-5 space-y-4" data-testid="workflow-actions">
      {/* Initial-assessment-complete banner */}
      {hasCompletedInitial && (
        <div
          className="flex items-start justify-between gap-3 p-3 rounded-lg border"
          style={{ backgroundColor: "#fffbeb", borderColor: "#fde68a" }}
          data-testid="initial-complete-banner"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: "#C79A3B" }}>
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </div>
            <div>
              <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#a16207]">
                Preliminary Assessment Complete
              </div>
              <div className="text-[13px] mt-0.5 text-[#92400e] leading-relaxed">
                Send the customer their portal access so they can review results and purchase the in-depth assessment.
              </div>
            </div>
          </div>
          {shouldOfferPortalAccess && (
            <button
              type="button"
              onClick={handleSendPortalShortcut}
              disabled={busy}
              className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-[12px] font-bold text-white whitespace-nowrap shadow-[0_4px_12px_rgba(199,154,59,0.30)] hover:opacity-95"
              style={{ backgroundColor: "#C79A3B" }}
              data-testid="send-portal-access-shortcut"
            >
              <Mail className="h-3.5 w-3.5" />
              Send Portal Access
            </button>
          )}
          {prospect.provisioned_tenant_id && (
            <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-semibold" style={{ backgroundColor: "#dcfce7", color: "#166534" }}>
              <Check className="h-3 w-3" /> Portal Sent
            </span>
          )}
        </div>
      )}

      {/* Next Step banner */}
      {config.nextStep && (
        <div className={`flex items-start gap-3 p-3 rounded-lg border ${
          config.gate
            ? "bg-[#f0fdf4] border-[#bbf7d0]"
            : "bg-[#fffbeb] border-[#fde68a]"
        }`}>
          <div
            className="mt-0.5 shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold text-white"
            style={{ backgroundColor: config.gate ? "#16a34a" : "#C79A3B" }}
          >
            {config.gate || "!"}
          </div>
          <div className="min-w-0">
            <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#64748b]">
              Next Step
            </div>
            <div className={`text-[13px] mt-0.5 leading-relaxed ${config.gate ? "text-[#166534]" : "text-[#92400e]"}`}>
              {config.nextStep}
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {transitions.map((t) => {
          if (t.productKey && !isAdmin) return null;
          const gated = isGated(t);
          // Gold for primary advancement actions
          const variant = t.variant === "primary" ? "gold" : t.variant;
          const goldStyle = variant === "gold" ? { backgroundColor: "#C79A3B" } : {};
          return (
            <div key={t.target} className="flex flex-col gap-1">
              <button
                type="button"
                disabled={busy || !!gated}
                onClick={() => handleAdvance(t)}
                className={`inline-flex items-center h-10 px-4 rounded-lg text-[13px] font-semibold transition-all ${ACTION_VARIANTS[variant] || ACTION_VARIANTS.secondary} ${gated ? "opacity-50 cursor-not-allowed" : ""}`}
                style={goldStyle}
                data-testid={`stage-action-${t.target.toLowerCase()}`}
              >
                <ActionIcon variant={variant} target={t.target} />
                {labelFor(t)}
              </button>
              {gated === "consultant" && (
                <span className="text-[11.5px] text-[#a16207] flex items-center gap-1">
                  <UserCheck className="h-3 w-3" /> Assign a consultant first
                </span>
              )}
              {gated === "scope" && (
                <span className="text-[11.5px] text-[#1e40af] flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Waiting for customer to confirm scope in their portal
                </span>
              )}
              {gated === true && (
                <span className="text-[11.5px] text-[#a16207] flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Complete the {t.requiresAssessment === "initial" ? "Initial" : "Detailed"} Assessment first
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// 6-step journey tracker — light theme, gold + navy palette
// ─────────────────────────────────────────────────────────────

const JourneyTracker = ({ stage }) => {
  const activeIdx = journeyIndexFor(stage);
  return (
    <div className="rounded-xl border border-[#e2e8f0] bg-white p-5">
      <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#64748b] mb-4">
        Journey Progress
      </div>
      <div className="flex items-start gap-0 relative">
        {JOURNEY_STEPS.map((s, idx) => {
          const isComplete = idx < activeIdx;
          const isActive = idx === activeIdx;
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center gap-2 relative z-10">
                <div
                  className="h-9 w-9 rounded-full flex items-center justify-center text-[12px] font-bold shrink-0"
                  style={
                    isComplete
                      ? { backgroundColor: "#C79A3B", color: "white" }
                      : isActive
                        ? { backgroundColor: "#0B1628", color: "white", boxShadow: "0 0 0 3px rgba(11,22,40,0.10)" }
                        : { backgroundColor: "#f1f5f9", color: "#94a3b8", border: "1.5px solid #e2e8f0" }
                  }
                >
                  {isComplete ? "✓" : idx + 1}
                </div>
                <span
                  className={`text-[11.5px] font-semibold text-center leading-tight min-w-[60px] ${
                    isComplete ? "text-[#a16207]" : isActive ? "text-[#0B1628]" : "text-[#4b5563]"
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {idx < JOURNEY_STEPS.length - 1 && (
                <div
                  className="h-[2px] flex-1 rounded-full mt-[18px]"
                  style={{ backgroundColor: idx < activeIdx ? "#C79A3B" : "#e2e8f0" }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Consultant Assignment — light theme rebuild
// ─────────────────────────────────────────────────────────────

const ConsultantAssignmentBlock = ({ prospect, session, onRefresh }) => {
  const [consultants, setConsultants] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/employees/lookup/consultants`)
      .then((r) => r.json())
      .then((d) => { setConsultants(d); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const handleAssign = async (field, consultantId) => {
    const emp = consultants.find((c) => c.id === consultantId);
    if (!emp) return;
    setAssigning(field);
    try {
      const payload = { prospect_id: prospect.id };
      payload[`${field}_id`] = consultantId;
      payload[`${field}_name`] = `${emp.first_name} ${emp.last_name}`;
      if (field === "free_assessment_consultant") {
        payload.assigned_consultant_id = consultantId;
        payload.assigned_consultant_name = `${emp.first_name} ${emp.last_name}`;
      }
      await crmApi.updateProspect(payload, session);
      toast.success(`${emp.first_name} ${emp.last_name} assigned`);
      onRefresh();
    } catch {
      toast.error("Failed to assign consultant");
    } finally {
      setAssigning(null);
    }
  };

  if (!loaded) return null;

  const phases = [
    { key: "free_assessment_consultant", label: "Free Assessment" },
    { key: "detailed_assessment_consultant", label: "In-Depth Assessment" },
    { key: "project_consultant", label: "Project" },
  ];

  return (
    <div className="rounded-xl border border-[#e2e8f0] bg-white p-5" data-testid="workspace-consultant-assignment">
      <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#64748b] mb-3">
        Consultant Assignments
      </div>
      <div className="space-y-2">
        {phases.map(({ key, label }) => {
          const currentId = prospect[`${key}_id`] || (key === "free_assessment_consultant" ? prospect.assigned_consultant_id : "");
          const currentName = prospect[`${key}_name`] || (key === "free_assessment_consultant" ? prospect.assigned_consultant_name : "");
          return (
            <div key={key} className="flex items-center gap-3">
              <span className="text-[12px] text-[#475569] w-[150px] shrink-0">{label}</span>
              {currentId ? (
                <span className="text-[13px] font-semibold text-[#16a34a] flex items-center gap-1.5 flex-1 min-w-0 truncate">
                  <Check className="h-3.5 w-3.5 shrink-0" /> {currentName || "Assigned"}
                </span>
              ) : (
                <span className="text-[13px] font-semibold text-[#dc2626] flex items-center gap-1.5 flex-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> Unassigned
                </span>
              )}
              <select
                value={currentId || ""}
                onChange={(e) => e.target.value && handleAssign(key, e.target.value)}
                disabled={assigning === key}
                className="h-8 rounded-md border border-[#e2e8f0] bg-white text-[12px] text-[#0f172a] px-2 w-[160px]"
                data-testid={`workspace-assign-${key}-select`}
              >
                <option value="">{currentId ? "Change…" : "Select consultant"}</option>
                {consultants.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name}
                  </option>
                ))}
              </select>
              {assigning === key && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#4b5563]" />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// AI Enrichment button — light theme
// ─────────────────────────────────────────────────────────────

const EnrichButton = ({ prospect, session, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await crmApi.enrichWithAI({ prospect_id: prospect.id }, session);
          toast.success("AI enrichment complete");
          onRefresh();
        } catch (e) {
          toast.error("Enrichment failed", { description: e?.message || "Try again." });
        } finally {
          setBusy(false);
        }
      }}
      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-semibold text-white"
      style={{ backgroundColor: "#0B1628" }}
    >
      {busy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
      {prospect.ai_enrichment ? "Re-enrich with AI" : "Enrich with AI"}
    </button>
  );
};

// ─────────────────────────────────────────────────────────────
// Main Workspace screen
// ─────────────────────────────────────────────────────────────

const TABS = [
  { key: "workflow", label: "Workflow" },
  { key: "intake", label: "Intake Data" },
  { key: "assessment", label: "Assessment" },
  { key: "activity", label: "Activity" },
];

export default function AdminWorkspace({ session, mode = "leads" }) {
  const navigate = useNavigate();
  const { prospectId } = useParams();
  const [prospect, setProspect] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [tick, setTick] = useState(0);
  const [tab, setTab] = useState("workflow");
  const [assessmentStatus, setAssessmentStatus] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = () => setTick((x) => x + 1);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      crmApi.getProspect({ prospect_id: prospectId }, session).catch(() => null),
      fetch(`${BACKEND}/api/pricing/public`).then((r) => r.json()).catch(() => null),
    ]).then(([p, pr]) => {
      if (!mounted) return;
      setProspect(p);
      setPricing(pr);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [prospectId, session, tick]);

  // Auto-detect mode from prospect stage if URL is ambiguous
  const effectiveMode = useMemo(() => {
    if (!prospect) return mode;
    return isCustomerStage(prospect.stage) ? "customers" : "leads";
  }, [prospect, mode]);

  if (loading || !prospect) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#4b5563]" />
      </div>
    );
  }

  const stage = normalizeStage(prospect.stage);
  const stageMeta = STAGE_BADGE[stage] || { label: stage, bg: "#f3f4f6", fg: "#475569" };
  const company = prospect.account?.name || "—";
  const industry = prospect.account?.industry || "—";

  const interactions = (prospect.interactions || []).slice().sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );

  return (
    <div className="space-y-6" data-testid="admin-workspace">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-[13px] text-[#64748b] flex-wrap">
        <Link to="/app" className="hover:text-[#0f172a]">Overview</Link>
        <span className="text-[#cbd5e1]">/</span>
        <Link
          to={effectiveMode === "customers" ? "/app/customers" : "/app/pipeline"}
          className="hover:text-[#0f172a]"
        >
          {effectiveMode === "customers" ? "Customers" : "Leads"}
        </Link>
        <span className="text-[#cbd5e1]">/</span>
        <span className="font-semibold text-[#0f172a] truncate max-w-[280px]">{company}</span>
      </div>

      {/* Header card — Dark Navy */}
      <div
        className="rounded-2xl overflow-hidden text-white"
        style={{ backgroundColor: "#0B1628" }}
      >
        <div className="px-7 py-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => navigate(effectiveMode === "customers" ? "/app/customers" : "/app/pipeline")}
              className="inline-flex items-center gap-1.5 text-[12px] text-[#4b5563] hover:text-white mb-2 transition-colors"
              data-testid="workspace-back-btn"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to {effectiveMode === "customers" ? "Customers" : "Leads"}
            </button>
            <h1 className="text-[26px] font-bold tracking-[-0.02em]">{company}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#cbd5e1]">
              {prospect.contact?.full_name && (
                <span className="inline-flex items-center gap-1.5">
                  <UserCheck className="h-3.5 w-3.5 text-[#4b5563]" />
                  {prospect.contact.full_name}
                </span>
              )}
              {prospect.contact?.email && (
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-[#4b5563]" />
                  {prospect.contact.email}
                </span>
              )}
              {prospect.contact?.phone && (
                <span className="inline-flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5 text-[#4b5563]" />
                  {prospect.contact.phone}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 capitalize text-[#4b5563]">
                {industry}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center px-3 py-1.5 rounded-full text-[11.5px] font-bold"
              style={{ backgroundColor: "rgba(199, 154, 59, 0.18)", color: "#E5BD63" }}
            >
              {stageMeta.label}
            </span>
            <button
              type="button"
              onClick={async () => {
                await crmApi.mockBookInitialCall({ prospect_id: prospect.id }, session);
                toast.success("Qualification call scheduled");
                refresh();
              }}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-[12px] font-semibold text-white border border-[#1a2842] hover:bg-[#152038] transition-colors"
              data-testid="workspace-book-call-btn"
            >
              <Calendar className="h-3.5 w-3.5" />
              Book Call
            </button>
          </div>
        </div>
      </div>

      {/* Journey + Consultant assignment — stacked for breathing room */}
      <JourneyTracker stage={stage} />
      <ConsultantAssignmentBlock prospect={prospect} session={session} onRefresh={refresh} />

      {/* Tabs */}
      <div className="rounded-2xl border border-[#e2e8f0] bg-white">
        <div className="border-b border-[#e2e8f0] px-5">
          <div className="flex items-center gap-6">
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`py-4 text-[13px] font-semibold border-b-2 transition-colors ${
                    active
                      ? "border-[#C79A3B] text-[#C79A3B]"
                      : "border-transparent text-[#64748b] hover:text-[#0f172a]"
                  }`}
                  data-testid={`workspace-tab-${t.key}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-5">
          {tab === "workflow" && (
            <div className="space-y-4">
              <StageActionsBlock
                session={session}
                prospect={prospect}
                onRefresh={refresh}
                assessmentStatus={assessmentStatus}
                pricing={pricing}
              />

              {/* Quick stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-4">
                  <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#64748b]">Current Stage</div>
                  <div className="mt-1.5 text-[14px] font-bold text-[#0f172a]">{stageMeta.label}</div>
                </div>
                <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-4">
                  <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#64748b]">Payments</div>
                  <div className="mt-1.5 text-[14px] font-bold text-[#0f172a] space-y-0.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] text-[#475569]">Assessment</span>
                      {prospect.payment?.detailed_assessment_paid ? (
                        <Check className="h-4 w-4 text-[#16a34a]" />
                      ) : (
                        <span className="text-[12px] text-[#4b5563]">—</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] text-[#475569]">Implementation</span>
                      {prospect.payment?.roadmap_paid ? (
                        <Check className="h-4 w-4 text-[#16a34a]" />
                      ) : (
                        <span className="text-[12px] text-[#4b5563]">—</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-4">
                  <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#64748b]">Activity</div>
                  <div className="mt-1.5 text-[14px] font-bold text-[#0f172a]">
                    {prospect.interactions?.length || 0} interactions
                  </div>
                  <div className="text-[11.5px] text-[#4b5563] mt-0.5">
                    Created {fmtRelativeDate(prospect.created_at)}
                  </div>
                </div>
              </div>

              {prospect.provisioned_tenant_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/app/tenants/${prospect.provisioned_tenant_id}`)}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-[12px] font-semibold text-white"
                  style={{ backgroundColor: "#C79A3B" }}
                  data-testid="workspace-open-portal-btn"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open Customer Portal
                </button>
              )}
            </div>
          )}

          {tab === "intake" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-4">
                  <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#64748b] mb-3">
                    Company Info
                  </div>
                  <dl className="space-y-2 text-[13px]">
                    <Row k="Company" v={prospect.account?.name} />
                    <Row k="Industry" v={prospect.account?.industry} />
                    <Row k="Size" v={prospect.intake_data?.company_size || prospect.account?.company_size} />
                    <Row
                      k="Website"
                      v={
                        prospect.intake_data?.website || prospect.account?.website ? (
                          <a
                            href={prospect.intake_data?.website || prospect.account?.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#C79A3B] hover:underline truncate inline-block max-w-[200px]"
                          >
                            {(prospect.intake_data?.website || prospect.account?.website).replace(/^https?:\/\//, "")}
                          </a>
                        ) : null
                      }
                    />
                  </dl>
                </div>
                <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-4">
                  <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#64748b] mb-3">
                    Contact
                  </div>
                  <dl className="space-y-2 text-[13px]">
                    <Row k="Name" v={prospect.contact?.full_name} />
                    <Row k="Email" v={prospect.contact?.email} />
                    <Row k="Phone" v={prospect.contact?.phone} />
                    <Row k="Role" v={prospect.intake_data?.role || prospect.contact?.title} />
                  </dl>
                </div>

                {prospect.intake_data?.pain_areas?.length > 0 && (
                  <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-4">
                    <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#64748b] mb-3">
                      Pain Areas
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {prospect.intake_data.pain_areas.map((p, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-semibold"
                          style={{ backgroundColor: "#fef3c7", color: "#a16207" }}
                        >
                          {p.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {prospect.intake_data?.urgency && (
                  <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-4">
                    <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#64748b] mb-3">
                      Urgency
                    </div>
                    <div className="flex items-center gap-2 text-[13px] text-[#0f172a]">
                      <Clock className="h-4 w-4 text-[#C79A3B]" />
                      {prospect.intake_data.urgency} days
                    </div>
                  </div>
                )}

                {prospect.intake_data?.notes && (
                  <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-4 md:col-span-2">
                    <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#64748b] mb-3">
                      Intake Notes
                    </div>
                    <p className="text-[13px] text-[#475569] leading-relaxed whitespace-pre-wrap">
                      {prospect.intake_data.notes}
                    </p>
                  </div>
                )}
              </div>

              {/* AI Enrichment */}
              <div className="rounded-lg bg-white border border-[#e2e8f0] p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[14px] font-bold text-[#0f172a]">AI-Enriched Insights</div>
                  <EnrichButton prospect={prospect} session={session} onRefresh={refresh} />
                </div>
                {prospect.ai_enrichment ? (
                  <div className="space-y-4">
                    {prospect.ai_enrichment.company_summary && (
                      <Field title="Company Profile" body={prospect.ai_enrichment.company_summary} />
                    )}
                    {prospect.ai_enrichment.industry_context && (
                      <Field title="Industry Context" body={prospect.ai_enrichment.industry_context} />
                    )}
                    {prospect.ai_enrichment.pain_point_analysis && (
                      <Field title="Pain Point Analysis" body={prospect.ai_enrichment.pain_point_analysis} />
                    )}
                    {prospect.ai_enrichment.recommended_focus && (
                      <Field title="Recommended Focus" body={prospect.ai_enrichment.recommended_focus} />
                    )}
                  </div>
                ) : (
                  <p className="text-[12.5px] text-[#4b5563]">
                    No AI enrichment yet. Generate insights to surface industry context and pain-point analysis.
                  </p>
                )}
              </div>

              {/* Customer-uploaded Assets (if tenant provisioned) */}
              {prospect.provisioned_tenant_id && (
                <div className="rounded-lg bg-white border border-[#e2e8f0] p-5" data-testid="workspace-customer-assets">
                  <div className="text-[14px] font-bold text-[#0f172a] mb-3">Customer-Uploaded Assets</div>
                  <FileAttachments
                    session={{ ...session, tenant: { id: prospect.provisioned_tenant_id } }}
                    context="tenant_asset"
                    contextId={prospect.provisioned_tenant_id}
                    label="Files from Customer Portal"
                    emptyHint="Customer has not uploaded any supporting assets yet. They can upload via the Assets section of their portal."
                    dataTestidPrefix="workspace-asset"
                  />
                </div>
              )}
            </div>
          )}

          {tab === "assessment" && (
            <div className="workspace-assessment-host">
              {/* The existing ProspectAssessment component renders inside a dark-styled Card.
                  Wrap it in a CSS scope that flips foreground colors to legible values on light bg. */}
              <ProspectAssessment
                session={session}
                prospect={prospect}
                onAssessmentStatusChange={setAssessmentStatus}
              />
            </div>
          )}

          {tab === "activity" && (
            <div className="space-y-3">
              {interactions.length === 0 ? (
                <div className="text-center py-10 text-[13px] text-[#4b5563]">
                  No activity recorded yet.
                </div>
              ) : (
                interactions.map((it) => {
                  const typeColor = {
                    assignment: "#C79A3B",
                    stage_change: "#1e40af",
                    payment: "#16a34a",
                    call: "#7c3aed",
                    email: "#0891b2",
                    note: "#64748b",
                  }[it.type] || "#C79A3B";
                  const typeIcon = {
                    assignment: "👤",
                    stage_change: "→",
                    payment: "$",
                    call: "☎",
                    email: "✉",
                    note: "✎",
                  }[it.type] || "•";
                  const displayType = (it.type || "event").replace(/_/g, " ");
                  const displayText = it.summary || it.subject || it.kind || "Interaction";
                  return (
                    <div
                      key={it.id}
                      className="flex items-start gap-3 rounded-lg border border-[#e2e8f0] bg-white p-3"
                      data-testid={`activity-${it.id}`}
                    >
                      <div
                        className="mt-0.5 h-7 w-7 rounded-full flex items-center justify-center shrink-0 text-[11.5px] font-bold text-white"
                        style={{ backgroundColor: typeColor }}
                      >
                        <span className="leading-none">{typeIcon}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider"
                          style={{ backgroundColor: `${typeColor}15`, color: typeColor }}
                        >
                          {displayType}
                        </span>
                        <div className="text-[13px] font-semibold text-[#0f172a] mt-1">
                          {displayText}
                        </div>
                        {it.body && (
                          <p className="text-[12.5px] text-[#475569] mt-0.5 leading-relaxed">{it.body}</p>
                        )}
                        <div className="text-[11.5px] text-[#4b5563] mt-1">
                          {fmtRelativeDate(it.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Light-theme readability override for the embedded Assessment card */}
      <style>{`
        .workspace-assessment-host .text-foreground { color: #0f172a !important; }
        .workspace-assessment-host .text-foreground\\/70 { color: #475569 !important; }
        .workspace-assessment-host .text-foreground\\/65 { color: #475569 !important; }
        .workspace-assessment-host .text-foreground\\/55 { color: #64748b !important; }
        .workspace-assessment-host .text-foreground\\/50 { color: #64748b !important; }
        .workspace-assessment-host .text-foreground\\/45 { color: #94a3b8 !important; }
        .workspace-assessment-host .text-foreground\\/40 { color: #94a3b8 !important; }
        .workspace-assessment-host .text-foreground\\/35 { color: #94a3b8 !important; }
        .workspace-assessment-host .bg-foreground\\/5 { background-color: #ffffff !important; }
        .workspace-assessment-host .bg-foreground\\/10 { background-color: #f1f5f9 !important; }
        .workspace-assessment-host .bg-muted { background-color: #f8fafc !important; }
        .workspace-assessment-host .bg-muted\\/60 { background-color: #f8fafc !important; }
        .workspace-assessment-host .border-border { border-color: #e2e8f0 !important; }
        .workspace-assessment-host .text-\\[var\\(--brand\\)\\] { color: #C79A3B !important; }
        .workspace-assessment-host .bg-\\[var\\(--brand\\)\\] { background-color: #C79A3B !important; }
      `}</style>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-3">
    <dt className="text-[#64748b]">{k}</dt>
    <dd className="text-[#0f172a] font-semibold text-right truncate max-w-[60%]">{v || "—"}</dd>
  </div>
);

const Field = ({ title, body }) => (
  <div>
    <div className="text-[11.5px] font-bold uppercase tracking-[0.10em] text-[#64748b] mb-1.5">{title}</div>
    <p className="text-[13px] text-[#475569] leading-relaxed">{body}</p>
  </div>
);
